import React from "react";
import { Box, IconButton, Portal } from "@chakra-ui/react";

interface IButtonDropdown {
  popoverContent: React.ReactNode;
  disabled?: boolean;
}

const GAP = 6;
const VIEWPORT_PADDING = 8;

const ButtonDropdown: React.FC<IButtonDropdown> = ({
  popoverContent,
  disabled,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [coords, setCoords] = React.useState({
    top: -9999,
    left: -9999,
    visibility: "hidden" as "hidden" | "visible",
  });

  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const menuWidth =
    typeof window !== "undefined" && window.innerWidth < 768 ? 240 : 200;
  const closeMenu = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  const calculatePosition = React.useCallback(() => {
    const button = buttonRef.current;
    const menu = menuRef.current;

    if (!button || !menu) return;

    const buttonRect = button.getBoundingClientRect();

    const menuWidth =
      typeof window !== "undefined" && window.innerWidth < 768 ? 240 : 200;
    const menuHeight = menu.offsetHeight;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let left = buttonRect.right - menuWidth;
    let top = buttonRect.bottom + GAP;

    // Se sair pela esquerda, abre alinhando pela esquerda do botão
    if (left < VIEWPORT_PADDING) {
      left = buttonRect.left;
    }

    // Se sair pela direita, empurra para dentro
    if (left + menuWidth > viewportWidth - VIEWPORT_PADDING) {
      left = viewportWidth - menuWidth - VIEWPORT_PADDING;
    }

    // Se não couber embaixo, abre em cima
    if (top + menuHeight > viewportHeight - VIEWPORT_PADDING) {
      top = buttonRect.top - menuHeight - GAP;
    }

    // Proteção superior
    if (top < VIEWPORT_PADDING) {
      top = VIEWPORT_PADDING;
    }

    setCoords({
      top,
      left,
      visibility: "visible",
    });
  }, []);

  const handleToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    setIsOpen((prev) => !prev);
  };

  React.useLayoutEffect(() => {
    if (!isOpen) return;

    requestAnimationFrame(() => {
      calculatePosition();
    });
  }, [isOpen, calculatePosition]);

  React.useEffect(() => {
    if (!isOpen) return;

    const handleOutside = (e: MouseEvent) => {
      const target = e.target as Node;

      if (
        !buttonRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        closeMenu();
      }
    };

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeMenu();
      }
    };

    document.addEventListener("mousedown", handleOutside, true);
    document.addEventListener("keydown", handleEsc);

    return () => {
      document.removeEventListener("mousedown", handleOutside, true);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [isOpen, closeMenu]);

  return (
    <>
      <IconButton
        ref={buttonRef}
        aria-label="menu"
        variant="ghost"
        size="sm"
        isDisabled={disabled}
        onClick={handleToggle}
        icon={
          <Box fontSize="20px" transform="rotate(90deg)">
            ⋮
          </Box>
        }
      />

      {isOpen && (
        <Portal>
          <Box
            ref={menuRef}
            position="fixed"
            top={`${coords.top}px`}
            left={`${coords.left}px`}
            visibility={coords.visibility}
            bg="white"
            borderRadius="md"
            boxShadow="2xl"
            zIndex={999999}
            width={`${menuWidth}px`}
            maxW={`${menuWidth}px`}
            overflow="hidden"
          >
            <Box py={1}>{popoverContent}</Box>
          </Box>
        </Portal>
      )}
    </>
  );
};

export default ButtonDropdown;
